import React, { useRef, useEffect } from "react"
import { ButtonWithPanel } from "./ButtonWithPanel"
import { FilterComposer, FilterFields } from "../FilterComposer/FilterComposer"
import { FilterExpression } from "../../../Models/Client/FilterExpression"
import styles from "./FilterChooser.module.css"
import { useBoolean } from "../../../Hooks/useBoolean"
import { useOutsideClick } from "../../../Hooks/useOutsideClick"
import { usePrevious } from "../../../Hooks/usePrevious"

/**
 * Configuration for filtering functionality
 */
export interface FilterChooserProps<M> {
    /**
     * Filters which are currently applied
     */
    applied: FilterExpression<M>[]
    /**
     * Configurations for all possible filters
     */
    fields: FilterFields<M>
    /**
     * Filter to be added when user clicks `Add a filter` button
     */
    defaultOnAdd: FilterExpression<M>
    classNameClosed?: string
    classNameOpen?: string
    /**
     * Callback function to handle changing filters
     */
    onChange: (value: FilterExpression<M>[]) => void
    testId?: string
    disabled?: boolean
}

export function FilterChooser<M>({
    applied,
    fields,
    onChange,
    defaultOnAdd,
    classNameClosed,
    classNameOpen,
    testId,
    disabled
}: FilterChooserProps<M>) {
    const [isOpen, , close, toggleOpen] = useBoolean(false)
    const wasOpen = usePrevious(isOpen)
    const ref = useRef<HTMLDivElement>(null)
    const isActive = applied.length > 0
    const firstFilter = isActive ? fields[applied[0].fieldName] : undefined

    useEffect(() => {
        if (isOpen && applied.length === 0) {
            if (wasOpen) {
                close()
                return
            }

            onChange([defaultOnAdd])
        }
    }, [applied.length, close, defaultOnAdd, isOpen, onChange, wasOpen])

    useOutsideClick(ref, close)

    return (
        <ButtonWithPanel
            ref={ref}
            isOpen={isOpen}
            onClickButton={toggleOpen}
            testId={testId}
            classNameClosed={classNameClosed}
            classNameOpen={classNameOpen}
            color={isActive ? "outline-pink" : "plain"}
            buttonText={
                firstFilter === undefined
                    ? "Filter"
                    : applied.length === 1
                    ? `Filter: ${firstFilter.label}`
                    : "Filter: multiple"
            }
            disabled={disabled}
            panelBody={
                <FilterComposer
                    className={
                        isActive
                            ? styles.FilterChooser__PanelContent_active
                            : ""
                    }
                    value={applied}
                    defaultFilter={defaultOnAdd}
                    fields={fields}
                    onChange={onChange}
                />
            }
        />
    )
}
